import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import { 
  CreditCard, 
  Calendar, 
  ShieldCheck, 
  AlertTriangle, 
  ArrowRight,
  Zap,
  CheckCircle2,
  Clock
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface Subscription {
  id: string;
  plan: string;
  status: string;
  current_period_end: string;
  stripe_subscription_id?: string;
}

export default function MinhaAssinaturaPage() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [canceling, setCanceling] = useState(false);

  async function loadSubscription() {
    if (!user) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      setSubscription(data);
    } catch (error: any) {
      console.error("Erro ao carregar assinatura:", error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadSubscription();
  }, [user]);

  async function handleManageSubscription() {
    setCanceling(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-stripe-portal");
      
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("Não foi possível carregar o portal de gestão.");
      }
    } catch (error: any) {
      toast({
        title: "Erro ao carregar gestão",
        description: error.message || "Tente novamente em instantes ou contate o suporte.",
        variant: "destructive",
      });
    } finally {
      setCanceling(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-700">
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-black tracking-tight text-slate-900 dark:text-white flex items-center gap-3">
          <CreditCard className="size-8 text-primary" />
          Gestão da Assinatura
        </h1>
        <p className="text-slate-500 dark:text-slate-400">Gerencie seu plano, pagamentos e renovações no portal seguro do Stripe.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Card Principal do Plano */}
        <Card className="lg:col-span-2 overflow-hidden border-none shadow-elevated bg-white/70 dark:bg-white/5 backdrop-blur-2xl">
          <div className="p-8 lg:p-10">
            <div className="flex flex-col sm:flex-row justify-between items-start gap-6 mb-10">
              <div className="space-y-4">
                <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 uppercase tracking-widest font-black text-[10px] py-1 px-3">
                  Plano Atual
                </Badge>
                <div className="flex items-center gap-4">
                  <div className="size-14 rounded-2xl bg-gradient-to-br from-primary to-primary-400 shadow-glow grid place-items-center">
                    <Zap className="size-7 text-white" />
                  </div>
                  <div>
                    <h2 className="text-4xl font-black text-slate-900 dark:text-white tracking-tight">
                      {subscription?.plan || "Plano Grátis"}
                    </h2>
                    <div className="flex items-center gap-2 mt-1">
                      <div className={`size-2 rounded-full ${subscription?.status === 'active' ? 'bg-success animate-pulse' : 'bg-slate-400'}`} />
                      <span className="text-sm font-bold uppercase tracking-wider opacity-60">
                        {subscription?.status === 'active' ? 'Ativa' : 'Inativa'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {subscription?.status === 'active' && (
                <div className="bg-white/50 dark:bg-black/20 p-6 rounded-2xl border border-white dark:border-white/5 shadow-inner min-w-[200px]">
                  <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 mb-2">
                    <Calendar className="size-4" />
                    <span className="text-[10px] font-black uppercase tracking-widest">Próxima Cobrança</span>
                  </div>
                  <div className="text-xl font-black text-slate-900 dark:text-white">
                    {subscription?.current_period_end 
                      ? new Date(subscription.current_period_end).toLocaleDateString('pt-BR')
                      : "--/--/----"}
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-8 border-t border-slate-200 dark:border-white/10">
              <div className="flex gap-4">
                <div className="size-10 rounded-xl bg-blue-500/10 grid place-items-center shrink-0">
                  <ShieldCheck className="size-5 text-blue-500" />
                </div>
                <div>
                  <h4 className="font-bold text-sm text-slate-900 dark:text-white">Pagamento Seguro</h4>
                  <p className="text-xs text-slate-500">Processado via Stripe com segurança internacional.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="size-10 rounded-xl bg-emerald-500/10 grid place-items-center shrink-0">
                  <CheckCircle2 className="size-5 text-emerald-500" />
                </div>
                <div>
                  <h4 className="font-bold text-sm text-slate-900 dark:text-white">Gestão Facilitada</h4>
                  <p className="text-xs text-slate-500">Altere cartões ou cancele no portal do cliente.</p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Card de Ações Rápidas */}
        <div className="space-y-6">
          {/* <Card className="p-6 border-none shadow-md bg-gradient-to-br from-primary/10 to-transparent backdrop-blur-xl border border-primary/20">
            <h3 className="font-black uppercase tracking-widest text-[11px] opacity-60 mb-4">Precisa de mais?</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6 leading-relaxed">
              Deseja aumentar seu limite de processamento ou adicionar mais usuários?
            </p>
            <Button className="w-full h-12 bg-primary hover:bg-primary-400 text-white font-black uppercase tracking-wider text-xs shadow-glow" onClick={() => window.location.href = '/planos'}>
              Ver outros planos
              <ArrowRight className="ml-2 size-4" />
            </Button>
          </Card> */}

          <Card className="p-6 border-none shadow-md bg-white/50 dark:bg-white/5 backdrop-blur-xl">
            <h3 className="font-black uppercase tracking-widest text-[11px] text-slate-500 mb-4">Gerenciar Assinatura</h3>
            <p className="text-xs text-slate-500 mb-6">
              Acesse o portal do Stripe para atualizar dados de pagamento ou gerenciar sua recorrência.
            </p>
            
            <Button 
              onClick={handleManageSubscription}
              disabled={subscription?.status !== 'active' || canceling}
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-black uppercase tracking-wider text-xs"
            >
              {canceling ? "Carregando..." : "Gerenciar no Stripe"}
            </Button>
          </Card>
        </div>
      </div>

      {/* Histórico Simples */}
      <div className="pt-10">
        <h3 className="text-xl font-black tracking-tight text-slate-900 dark:text-white mb-6 flex items-center gap-2">
          <Clock className="size-5 opacity-40" />
          Suporte e Dúvidas
        </h3>
        <Card className="p-6 border-none bg-white/40 dark:bg-white/5 backdrop-blur-md">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Tem algum problema com sua cobrança ou precisa de uma nota fiscal específica? 
            Entre em contato com nosso time financeiro: <a href="mailto:financeiro@seusite.com.br" className="text-primary font-bold hover:underline">financeiro@seusite.com.br</a>
          </p>
        </Card>
      </div>
    </div>
  );
}
